import {createStyles} from '@mantine/core';
import React, {ReactNode, useState} from 'react';

import {redirectForDownload} from 'BenchlingUtil';
import ValidationErrorsTable, {SpreadsheetType} from 'bulk-import/panes/ValidationErrorsTable';
import {
  EMPTY_SPREADSHEET_VALIDATION_ERROR_INFO,
  ErrorData,
  RowErrorCode,
  SpreadsheetValidationError,
} from 'bulk-import/SpreadsheetBulkImportConstants';
import navigateToURL from 'lib/navigateToURL';
import notifications from 'notifications';
import {FONT_PROXIMA_13, FONT_PROXIMA_14} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerActionType, InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARK, GRAY_DARKER, NEAR_BLACK} from 'ui-library/Colors';
import FileChooser from 'ui-library/FileChooser';
import FileList from 'ui-library/FileList';
import FileRow from 'ui-library/FileRow';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import * as ImportUtil from 'util/ImportUtil';
import * as net from 'util/Net';

import {SharedMonomerCsvValidationOutput, SharedMonomerValidationOutput} from '../SharedMonomerConstants';

export interface SharedMonomerCreateFromCSVPageProps {
  pageContainer: ReactNode;
  libraryUrl: string;
  bannerText: string;
  bannerSupportingInfo?: string;
  templateCsvLink: string;
  validate: (filename: string, url: string) => Promise<SharedMonomerCsvValidationOutput>;
  // Returns the number of created monomers so this page stays agnostic to the endpoint's response shape.
  createFromFile: (url: string, filename: string) => Promise<number>;
  dataTestComponent: string;
}

export default function SharedMonomerCreateFromCSVPage({
  pageContainer,
  libraryUrl,
  bannerText,
  bannerSupportingInfo,
  templateCsvLink,
  validate,
  createFromFile,
  dataTestComponent,
}: SharedMonomerCreateFromCSVPageProps): JSX.Element {
  const {classes, cx} = useStyles();
  const [csvValidationOutput, setCsvValidationOutput] = useState<SharedMonomerCsvValidationOutput | null>(
    null
  );
  const [importUrl, setImportUrl] = useState<string | null>(null);
  const [isValidationLoading, setIsValidationLoading] = useState<boolean>(false);
  const [file, setFile] = useState<File | null>(null);
  const [errorData, setErrorData] = useState<ErrorData>(EMPTY_SPREADSHEET_VALIDATION_ERROR_INFO);

  const canSubmit =
    file != null &&
    !isValidationLoading &&
    csvValidationOutput != null &&
    csvValidationOutput.csvFormattingValidationError == null &&
    csvValidationOutput.monomerValidationOutputs?.every((output) => output.isValid);

  const setErrorsForMonomerValidation = (
    monomerValidationOutputs: Array<SharedMonomerValidationOutput> | null
  ): void => {
    if (monomerValidationOutputs == null) {
      return;
    }
    const otherErrors: Array<SpreadsheetValidationError> = [];
    for (const [row, monomerOutput] of monomerValidationOutputs.entries()) {
      if (monomerOutput.isValid) {
        continue;
      }
      const currentRowErrors: Array<SpreadsheetValidationError> = [];
      for (const errorMessage of Object.values(monomerOutput.errors)) {
        if (errorMessage != null) {
          currentRowErrors.push({row, errorCode: RowErrorCode.OTHER_ERROR, errorMessage});
        }
      }
      if (currentRowErrors.length === 0) {
        // If we didn't detect any property errors and isValid is false, we must be missing required fields.
        currentRowErrors.push({
          row,
          errorCode: RowErrorCode.OTHER_ERROR,
          errorMessage: 'Row is missing at least one required field value.',
        });
      }
      otherErrors.push(...currentRowErrors);
    }
    setErrorData({...EMPTY_SPREADSHEET_VALIDATION_ERROR_INFO, otherErrors});
  };

  const uploadAndValidateCsv = async (file: File): Promise<void> => {
    setFile(file);
    setIsValidationLoading(true);
    const importUrl = await ImportUtil.upload(file);
    if (importUrl) {
      try {
        setImportUrl(importUrl);
        const output = await validate(file.name, importUrl);
        setCsvValidationOutput(output);
        setErrorsForMonomerValidation(output.monomerValidationOutputs);
        // eslint-disable-next-line benchlint/always-throw-from-catch
      } catch (e) {
        if (net.isServerRequestError(e)) {
          notifications.notifyError(e);
        }
      } finally {
        setIsValidationLoading(false);
      }
    }
  };

  const createMonomers = async (): Promise<void> => {
    if (importUrl != null && file != null) {
      try {
        LoadingScreen.show('Importing...');
        const count = await createFromFile(importUrl, file.name);
        notifications.success(`Imported ${count} monomers`);
        navigateToURL(libraryUrl);
        // eslint-disable-next-line benchlint/always-throw-from-catch
      } catch (e) {
        notifications.notifyError(e);
      } finally {
        LoadingScreen.hide();
      }
    }
  };

  const createButton = (
    <Button
      buttonStyle={ButtonStyle.SECONDARY}
      text="Import monomers"
      size={ButtonSize.MEDIUM}
      disabled={!canSubmit}
      onClick={createMonomers}
      dataTestAttrs={{
        component: dataTestComponent,
        element: 'createButton',
      }}
    />
  );

  const fileSection = (
    <div className={classes.fileSection}>
      <div className={classes.bannerContainer}>
        <InlineBanner
          bannerType={InlineBannerType.INFO}
          text={bannerText}
          supportingInfo={bannerSupportingInfo}
          action={{
            actionType: InlineBannerActionType.CUSTOM,
            actionText: 'Download CSV template',
            actionOnClick: () => {
              redirectForDownload(templateCsvLink);
            },
          }}
        />
      </div>
      <div className={classes.fileChooserContainer}>
        {file == null ? (
          <div className={cx(classes.flex1, classes.fileChooser)}>
            <FileChooser onSelectFiles={uploadAndValidateCsv} multiple={false} />
          </div>
        ) : (
          <div className={classes.flex1}>
            <FileList>
              <FileRow
                file={file}
                onRequestRemove={() => {
                  setFile(null);
                }}
              />
            </FileList>
          </div>
        )}
      </div>
    </div>
  );

  const validationSection = (
    <div>
      {file == null ? null : isValidationLoading ? (
        <CenteredHorizontalThrobber />
      ) : csvValidationOutput?.csvFormattingValidationError != null ? (
        <InlineBanner
          bannerType={InlineBannerType.ERROR}
          text={csvValidationOutput.csvFormattingValidationError}
        />
      ) : (
        <ValidationErrorsTable
          spreadsheetType={SpreadsheetType.SPREADSHEET}
          resolvableErrorStatuses={{}}
          errorData={errorData}
        />
      )}
    </div>
  );

  return (
    <div className={classes.pageContainer} {...getTestProps({component: dataTestComponent, element: 'root'})}>
      {pageContainer}
      <div className={classes.middleSection}>
        {fileSection}
        <span className={classes.uploadReviewText}>Upload review</span>
        <span className={classes.subtext}>Review spreadsheet interpretation and errors</span>
        {validationSection}
      </div>
      <div className={classes.submitBar}>
        <div className={classes.buttonsContainer}>
          <Button
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            onClick={() => {
              navigateToURL(libraryUrl);
            }}
            size={ButtonSize.MEDIUM}
            text="Cancel"
          />
          {createButton}
        </div>
      </div>
    </div>
  );
}

const useStyles = createStyles({
  pageContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflowY: 'scroll',
  },
  submitBar: {
    flex: '0 0 auto',
    boxSizing: 'border-box',
    padding: 16,
    gap: 10,
    boxShadow: '0px -2px 4px rgba(92, 124, 131, 0.1)',
  },
  buttonsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  middleSection: {
    marginLeft: 40,
    marginRight: 40,
    flex: 1,
    flexDirection: 'column',
    display: 'flex',
  },
  uploadReviewText: {
    ...FONT_PROXIMA_14,
    color: NEAR_BLACK,
    marginBottom: 4,
  },
  subtext: {
    ...FONT_PROXIMA_13,
    color: GRAY_DARKER,
    marginBottom: 16,
  },
  columnSubtext: {
    ...FONT_PROXIMA_13,
    color: GRAY_DARK,
  },
  fileSection: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: 4,
  },
  fileChooserContainer: {
    maxWidth: 450,
    display: 'flex',
    minHeight: 100,
    alignItems: 'center',
  },
  flex1: {
    flex: 1,
  },
  fileChooser: {
    marginLeft: -10,
  },
  bannerContainer: {
    maxWidth: 1050,
  },
});
